import { useLayoutEffect, useRef, useState } from 'react';
import { Tabs, TabsList, TabsTrigger } from '@/components/shadcn/tabs';
import { LoginForm } from '@/components/auth/LoginForm';
import { RegisterForm } from '@/components/auth/RegisterForm';
import { cn } from '@/lib/utils';

export type AuthTab = 'login' | 'register';

interface AuthTabSwitcherProps {
  value: AuthTab;
  onValueChange: (value: AuthTab) => void;
  stayOnCurrentPage?: boolean;
  onAuthenticated?: () => void;
  onForgotPassword?: () => void;
}

function AuthTabPanels({
  value,
  stayOnCurrentPage,
  onAuthenticated,
  onForgotPassword,
}: {
  value: AuthTab;
  stayOnCurrentPage?: boolean;
  onAuthenticated?: () => void;
  onForgotPassword?: () => void;
}) {
  const loginRef = useRef<HTMLDivElement>(null);
  const registerRef = useRef<HTMLDivElement>(null);
  const [panelHeight, setPanelHeight] = useState<number | undefined>(undefined);

  useLayoutEffect(() => {
    const activeEl = value === 'login' ? loginRef.current : registerRef.current;
    if (!activeEl) return;

    const updateHeight = () => setPanelHeight(activeEl.offsetHeight);

    updateHeight();

    const observer = new ResizeObserver(updateHeight);
    observer.observe(activeEl);

    return () => observer.disconnect();
  }, [value]);

  const panelTransition =
    'transition-[opacity,transform] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none';

  return (
    <div
      className="overflow-hidden transition-[height] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none"
      style={{ height: panelHeight ?? 'auto' }}
    >
      <div className="relative">
        <div
          ref={loginRef}
          className={cn(
            panelTransition,
            value === 'login'
              ? 'relative z-10 opacity-100 translate-y-0'
              : 'pointer-events-none absolute inset-x-0 top-0 z-0 opacity-0 translate-y-2'
          )}
          aria-hidden={value !== 'login'}
        >
          <LoginForm
            stayOnCurrentPage={stayOnCurrentPage}
            onSuccess={onAuthenticated}
            onForgotPassword={onForgotPassword}
          />
        </div>
        <div
          ref={registerRef}
          className={cn(
            panelTransition,
            value === 'register'
              ? 'relative z-10 opacity-100 translate-y-0'
              : 'pointer-events-none absolute inset-x-0 top-0 z-0 opacity-0 -translate-y-2'
          )}
          aria-hidden={value !== 'register'}
        >
          <RegisterForm />
        </div>
      </div>
    </div>
  );
}

export function AuthTabSwitcher({
  value,
  onValueChange,
  stayOnCurrentPage,
  onAuthenticated,
  onForgotPassword,
}: AuthTabSwitcherProps) {
  return (
    <Tabs
      value={value}
      onValueChange={(v) => onValueChange(v as AuthTab)}
      className="mb-6"
    >
      <TabsList className="relative grid h-auto w-full grid-cols-2 rounded-full bg-gc-200/60 p-1">
        <span
          aria-hidden
          className="pointer-events-none absolute inset-y-1 left-1 w-[calc((100%-0.5rem)/2)] rounded-full bg-white shadow-sm transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none"
          style={{
            transform: value === 'register' ? 'translateX(100%)' : 'translateX(0)',
          }}
        />
        <TabsTrigger
          value="login"
          className="relative z-10 rounded-full py-2 text-muted-foreground transition-colors duration-200 data-[state=active]:bg-transparent data-[state=active]:text-primary-500 data-[state=active]:shadow-none data-[state=active]:font-semibold"
        >
          Masuk
        </TabsTrigger>
        <TabsTrigger
          value="register"
          className="relative z-10 rounded-full py-2 text-muted-foreground transition-colors duration-200 data-[state=active]:bg-transparent data-[state=active]:text-primary-500 data-[state=active]:shadow-none data-[state=active]:font-semibold"
        >
          Daftar
        </TabsTrigger>
      </TabsList>

      <div className="mt-2">
        <AuthTabPanels
          value={value}
          stayOnCurrentPage={stayOnCurrentPage}
          onAuthenticated={onAuthenticated}
          onForgotPassword={onForgotPassword}
        />
      </div>
    </Tabs>
  );
}
